import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Container,
  Typography,
  Card,
  CardContent,
  Box,
  Button,
  Grid,
  Paper,
} from '@mui/material';
import api from '../services/api';

const RecipeDetail = () => {
  const { id, token } = useParams();
  const navigate = useNavigate();
  const [recipe, setRecipe] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchRecipe = useCallback(async () => {
    try {
      let response;
      if (token) {
        response = await api.get(`/recipes/shared/${token}/`);
      } else {
        response = await api.get(`/recipes/${id}/`);
      }
      setRecipe(response.data);
      setLoading(false);
    } catch (error) {
      setLoading(false);
    }
  }, [id, token]);

  useEffect(() => {
    fetchRecipe();
  }, [fetchRecipe]);

  if (loading) {
    return (
      <Container>
        <Typography>Loading...</Typography>
      </Container>
    );
  }

  if (!recipe) {
    return (
      <Container>
        <Typography>Recipe not found</Typography>
      </Container>
    );
  }

  const ingredients = recipe.ingredients.split('\n').filter((line) => line.trim());

  return (
    <Container maxWidth="md" sx={{ mt: 4, mb: 4 }}>
      {!token && (
        <Button onClick={() => navigate('/recipes')} sx={{ mb: 2 }}>
          ← Back to Recipes
        </Button>
      )}

      {(recipe.image_url_display || recipe.image_url) && (
        <Box
          component="img"
          src={recipe.image_url_display || recipe.image_url}
          alt={recipe.title}
          sx={{
            width: '100%',
            maxHeight: 400,
            objectFit: 'cover',
            borderRadius: 2,
            mb: 3,
          }}
        />
      )}

      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h4" gutterBottom>
            {recipe.title}
          </Typography>
          <Typography variant="body2" color="text.secondary" gutterBottom>
            by {recipe.created_by_name}
          </Typography>
          {recipe.description && (
            <Typography variant="body1" sx={{ mt: 2 }}>
              {recipe.description}
            </Typography>
          )}
          <Box display="flex" gap={2} sx={{ mt: 2 }}>
            {recipe.prep_time && (
              <Typography variant="body2">
                ⏱️ Prep: {recipe.prep_time} {recipe.prep_time_unit === 'hours' ? 'hr' : 'min'}
              </Typography>
            )}
            {recipe.cook_time && (
              <Typography variant="body2">
                🔥 Cook: {recipe.cook_time} {recipe.cook_time_unit === 'hours' ? 'hr' : 'min'}
              </Typography>
            )}
            {recipe.servings && (
              <Typography variant="body2">
                👥 Serves: {recipe.servings}
              </Typography>
            )}
          </Box>
        </CardContent>
      </Card>

      <Grid container spacing={3}>
        <Grid item xs={12} md={6}>
          <Paper sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom>
              Ingredients
            </Typography>
            <Box component="ul" sx={{ pl: 2 }}>
              {ingredients.map((ingredient, index) => (
                <li key={index}>
                  <Typography variant="body1">{ingredient}</Typography>
                </li>
              ))}
            </Box>
          </Paper>
        </Grid>
        <Grid item xs={12} md={6}>
          <Paper sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom>
              Instructions
            </Typography>
            <Typography
              variant="body1"
              sx={{ whiteSpace: 'pre-line' }}
            >
              {recipe.instructions}
            </Typography>
          </Paper>
        </Grid>
      </Grid>
    </Container>
  );
};

export default RecipeDetail;
